// frontend/src/lib/severityModel.js
/**
 * Gravity-weighted severity model (deterministic)
 *
 * Exports:
 * - named: computeSeverityFromAnswers
 * - default: computeSeverityFromAnswers
 */

function clamp(n, a, b) {
    const x = Number(n);
    if (!Number.isFinite(x)) return a;
    return Math.max(a, Math.min(b, x));
  }
  
  function toNum(x, fallback = 0) {
    const n = Number(x);
    return Number.isFinite(n) ? n : fallback;
  }
  
  function gravityForQuestion(q) {
    const g = toNum(q?.gravity, 7);
    return clamp(g, 1, 10);
  }
  
  export function computeSeverityFromAnswers({ answers, activeQuestions }) {
    const a = answers && typeof answers === "object" ? answers : {};
    const qs = Array.isArray(activeQuestions) ? activeQuestions : [];
  
    const gravById = new Map();
    qs.forEach((q) => {
      const id = q?.id;
      if (id == null) return;
      gravById.set(String(id), gravityForQuestion(q));
    });
  
    const ids = qs.map((q) => String(q?.id)).filter(Boolean);
  
    let yesCount = 0;
    const failedIds = [];
    const unsureIds = [];
  
    ids.forEach((id) => {
      const v = a[id];
      if (v === true || v === "YES") yesCount += 1;
      else if (v === false || v === "NO") failedIds.push(id);
      else if (v === "unsure" || v === "UNSURE") unsureIds.push(id);
    });
  
    const total = ids.length || 0;
    const failedCount = failedIds.length;
    const unsureCount = unsureIds.length;
  
    const failedGravAvg =
      failedCount === 0
        ? 0
        : failedIds.reduce((acc, id) => acc + (gravById.get(String(id)) || 7), 0) / failedCount;
  
    const unsureGravAvg =
      unsureCount === 0
        ? 0
        : unsureIds.reduce((acc, id) => acc + (gravById.get(String(id)) || 7), 0) / unsureCount;
  
    const failedPressure = total ? clamp(failedCount / total, 0, 1) : 0;
    const unsurePressure = total ? clamp(unsureCount / total, 0, 1) : 0;
  
    const failedGravityNorm = clamp(failedGravAvg / 10, 0, 1);
    const unsureGravityNorm = clamp(unsureGravAvg / 10, 0, 1);
  
    const score0to1 =
      0.70 * (failedPressure * failedGravityNorm) +
      0.30 * (unsurePressure * unsureGravityNorm);
  
    const baseline = failedCount + unsureCount > 0 ? 0.08 : 0.0;
  
    const severityScore = clamp(
      Math.round(clamp(score0to1 + baseline, 0, 1) * 100),
      0,
      100
    );
  
    let tier = 1;
    if (severityScore >= 80) tier = 5;
    else if (severityScore >= 60) tier = 4;
    else if (severityScore >= 40) tier = 3;
    else if (severityScore >= 20) tier = 2;
  
    return {
      severity_tier: tier,
      severity_score_0_100: severityScore,
      explain: {
        total_questions: total,
        yes_count: yesCount,
        failed_count: failedCount,
        unsure_count: unsureCount,
        failed_gravity_avg: failedCount ? Math.round(failedGravAvg * 10) / 10 : 0,
        unsure_gravity_avg: unsureCount ? Math.round(unsureGravAvg * 10) / 10 : 0,
        failed_pressure: Math.round(failedPressure * 1000) / 1000,
        unsure_pressure: Math.round(unsurePressure * 1000) / 1000,
        baseline_added: baseline,
      },
      failed_question_ids: failedIds,
      unsure_question_ids: unsureIds,
    };
  }
  
  // Default export as well (prevents import mismatch issues)
  export default computeSeverityFromAnswers;
  